import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "flex items-center gap-2 text-sm text-gray-600" }, [
  /*#__PURE__*/_createElementVNode("p", { class: "font-semibold text-black" }, "Legenda:"),
  /*#__PURE__*/_createElementVNode("span", { class: "w-2.5 h-2.5 rounded-full border border-green-500 bg-green-100 inline-block" }),
  /*#__PURE__*/_createTextVNode(" Em vigência ")
], -1)
const _hoisted_4 = {
  class: "bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]",
  "data-testid": "qualification-link-policy-card"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold text-gray-800" }, "Vincular política", -1)
const _hoisted_6 = { class: "flex flex-col gap-4" }
const _hoisted_7 = {
  key: 1,
  class: "flex flex-col items-center justify-center py-12 text-center text-gray-500",
  "data-testid": "qualification-empty-state"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", { class: "text-base font-semibold text-gray-700" }, "Nenhuma política encontrada", -1)
const _hoisted_9 = { class: "text-sm text-gray-500 mt-1" }
const _hoisted_10 = ["onClick"]

import { ref, computed, onMounted, watch } from 'vue'
import { mdiPencilOutline, mdiEmailOpenMultipleOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import QualificationTransferTableSkeleton from '@/components/designSystem/Skeleton/QualificationTransferTableSkeleton.vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { formatIsoDateOnlyBR } from '@/utils/date'
import type {
  QualificationTransferPolicyItem,
  QualificationTransferLink
} from '@/Network/Types/Responses/QualificationTransferResponses'
import QualificationPolicyModal from './QualificationPolicyModal.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'QualificationTab',
  props: {
    scheduleId: {}
  },
  setup(__props: any) {

const props = __props;



// `toast` fica apenas para o sucesso e para as validações locais do formulário;
// todo erro vindo da API passa pelo `notifyError` (rule `error-handling.md`).
const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const searchQuery = ref('')
const policies = ref<QualificationTransferLink[]>([])

const columns = [
  { key: 'policyName', label: 'Nome' },
  { key: 'period', label: 'Período' },
  { key: 'linkedBy', label: 'Vinculado por' }
]

const filteredPolicies = computed(() => {
  if (!searchQuery.value) return policies.value
  const query = searchQuery.value.toLowerCase()
  return policies.value.filter((p) => p.policyName.toLowerCase().includes(query))
})

const fetchPolicies = async () => {
  try {
    isLoading.value = true
    const response = await Network.qualificationTransferPolicy.listScheduleLinks(props.scheduleId)
    policies.value = response.data
  } catch (e) {
    notifyError(e, 'Não foi possível carregar as políticas de qualificação.')
  } finally {
    isLoading.value = false
  }
}

const handleToggle = async (link: QualificationTransferLink, value: boolean) => {
  const index = policies.value.findIndex((l) => l.id === link.id)
  if (index === -1) return
  policies.value[index] = { ...policies.value[index], isActive: value }
  try {
    await Network.qualificationTransferPolicy.toggleActive(link.policyId, { isActive: value })
  } catch (e) {
    policies.value[index] = { ...policies.value[index], isActive: !value }
    notifyError(e, 'Não foi possível alterar o status da política.')
  }
}

const showEditModal = ref(false)
const selectedPolicy = ref<QualificationTransferPolicyItem | null>(null)
const selectedLinkId = ref<number | null>(null)

const showLinkCard = ref(false)
const selectedLinkPolicyId = ref<number | null>(null)
const linkStartDate = ref('')
const linkEndDate = ref('')
const isLinkIndefiniteEnd = ref(false)
const isLinkSubmitting = ref(false)
const availablePolicies = ref<QualificationTransferPolicyItem[]>([])
const isLoadingAvailablePolicies = ref(false)

const resetLinkForm = () => {
  selectedLinkPolicyId.value = null
  linkStartDate.value = ''
  linkEndDate.value = ''
  isLinkIndefiniteEnd.value = false
}

// O `v-menu` só emite `update:model-value` quando ele mesmo decide abrir/fechar
// (clique no ativador, clique fora). Quando o fechamento parte de nós
// (`handleCloseLinkCard` ou o sucesso de `handleSubmitLink`) nenhum evento é
// emitido — o `watch` é o único ponto que cobre os dois sentidos.
watch(showLinkCard, (open) => {
  if (open) {
    void loadAvailablePolicies()
  } else {
    resetLinkForm()
  }
})

const loadAvailablePolicies = async () => {
  try {
    isLoadingAvailablePolicies.value = true
    const response = await Network.qualificationTransferPolicy.list()
    availablePolicies.value = response.data
  } catch (e) {
    notifyError(e, 'Não foi possível carregar as políticas disponíveis.')
  } finally {
    isLoadingAvailablePolicies.value = false
  }
}

const handleCloseLinkCard = () => {
  showLinkCard.value = false
}

const handleLinkIndefiniteToggle = (value: boolean) => {
  if (value) linkEndDate.value = ''
}

const handleSubmitLink = async () => {
  if (!selectedLinkPolicyId.value) {
    toast.error('Selecione uma política.')
    return
  }
  if (!linkStartDate.value) {
    toast.error('Informe a data de início da vigência.')
    return
  }
  if (!isLinkIndefiniteEnd.value && !linkEndDate.value) {
    toast.error('Informe a data de fim da vigência ou marque como indeterminado.')
    return
  }

  try {
    isLinkSubmitting.value = true
    await Network.qualificationTransferPolicy.createScheduleLink(props.scheduleId, {
      policyId: selectedLinkPolicyId.value,
      startDate: linkStartDate.value,
      endDate: isLinkIndefiniteEnd.value ? undefined : linkEndDate.value || undefined
    })
    toast.success('Política vinculada com sucesso!')
    handleCloseLinkCard()
    await fetchPolicies()
  } catch (e) {
    notifyError(e, 'Não foi possível vincular a política.')
  } finally {
    isLinkSubmitting.value = false
  }
}

const handleEditPolicy = (link: QualificationTransferLink) => {
  selectedLinkId.value = link.id
  selectedPolicy.value = {
    id: link.policyId,
    policyName: link.policyName,
    company: '',
    startDate: link.startDate,
    endDate: link.endDate,
    createdBy: '',
    isActive: link.isActive
  }
  showEditModal.value = true
}

const handleCloseEditModal = () => {
  showEditModal.value = false
  selectedPolicy.value = null
  selectedLinkId.value = null
}

const handlePolicySaved = async () => {
  handleCloseEditModal()
  await fetchPolicies()
}

onMounted(fetchPolicies)

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_switch = _resolveComponent("v-switch")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(SearchInput, {
      modelValue: searchQuery.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
      label: "Buscar política",
      density: "compact",
      "data-testid": "qualification-search-input",
      class: "w-[320px]"
    }, null, 8, ["modelValue"]),
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createVNode(_component_v_menu, {
        modelValue: showLinkCard.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showLinkCard).value = $event)),
        "close-on-content-click": false,
        location: "bottom end",
        offset: "8",
        attach: "#app"
      }, {
        activator: _withCtx(({ props: menuProps }) => [
          _createVNode(OutlineButton, _mergeProps(menuProps, {
            text: "Vincular política",
            height: "32px",
            class: "text-sm !w-auto !px-4 !py-1",
            "data-testid": "qualification-link-policy-button"
          }), null, 16)
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", { class: "flex justify-between items-center mb-3" }, [
              _hoisted_5,
              _createElementVNode("button", {
                class: "text-gray-400 hover:text-gray-700",
                "data-testid": "qualification-link-policy-close-button",
                onClick: handleCloseLinkCard
              }, " ✕ ")
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_v_autocomplete, {
                modelValue: selectedLinkPolicyId.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedLinkPolicyId).value = $event)),
                items: availablePolicies.value,
                "item-title": "policyName",
                "item-value": "id",
                label: "Selecionar uma política",
                variant: "outlined",
                density: "compact",
                "hide-details": "",
                loading: isLoadingAvailablePolicies.value,
                "data-testid": "qualification-link-policy-select"
              }, null, 8, ["modelValue", "items", "loading"]),
              _createVNode(GenericInput, {
                modelValue: linkStartDate.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((linkStartDate).value = $event)),
                type: "date",
                label: "Início da vigência",
                density: "compact",
                "hide-details": "",
                "data-testid": "qualification-link-start-date"
              }, null, 8, ["modelValue"]),
              _createVNode(GenericInput, {
                modelValue: linkEndDate.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((linkEndDate).value = $event)),
                type: "date",
                label: "Fim da vigência",
                density: "compact",
                "hide-details": "",
                disabled: isLinkIndefiniteEnd.value,
                "data-testid": "qualification-link-end-date"
              }, null, 8, ["modelValue", "disabled"]),
              _createVNode(DsSwitch, {
                modelValue: isLinkIndefiniteEnd.value,
                "onUpdate:modelValue": [
                  _cache[4] || (_cache[4] = ($event: any) => ((isLinkIndefiniteEnd).value = $event)),
                  handleLinkIndefiniteToggle
                ],
                label: "Fim de vigência indeterminado",
                "data-testid": "qualification-link-indefinite-toggle"
              }, null, 8, ["modelValue"]),
              _createVNode(FilledButton, {
                text: "Incluir política",
                color: "primary30",
                height: "40px",
                loading: isLinkSubmitting.value,
                "data-testid": "qualification-link-submit-button",
                onClick: handleSubmitLink
              }, null, 8, ["loading"])
            ])
          ])
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    (isLoading.value)
      ? (_openBlock(), _createBlock(QualificationTransferTableSkeleton, { key: 0 }))
      : (policies.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(_component_v_icon, {
              icon: _unref(mdiEmailOpenMultipleOutline),
              size: "48",
              color: "grey-lighten-1",
              class: "mb-3"
            }, null, 8, ["icon"]),
            _hoisted_8,
            _createElementVNode("p", _hoisted_9, _toDisplayString(searchQuery.value ? 'Tente buscar com outro termo.' : 'Vincule uma política para começar.'), 1)
          ]))
        : (_openBlock(), _createBlock(TableModular, {
            key: 2,
            rows: filteredPolicies.value,
            columns: columns,
            "actions-label": "Ações",
            "data-testid": "qualification-table"
          }, {
            "column-period": _withCtx(({ row }) => [
              _createTextVNode(_toDisplayString(_unref(formatIsoDateOnlyBR)(row.startDate)) + " à " + _toDisplayString(_unref(formatIsoDateOnlyBR)(row.endDate)), 1)
            ]),
            actions: _withCtx(({ row }) => [
              _createElementVNode("button", {
                "data-testid": "qualification-edit-button",
                class: "text-primary30",
                onClick: ($event: any) => (handleEditPolicy(row as QualificationTransferLink))
              }, [
                _createVNode(_component_v_icon, {
                  size: "20",
                  icon: _unref(mdiPencilOutline)
                }, null, 8, ["icon"])
              ], 8, _hoisted_10),
              _createVNode(_component_v_switch, {
                "model-value": row.isActive,
                color: "primary30",
                density: "compact",
                "hide-details": "",
                "data-testid": "qualification-toggle-button",
                "onUpdate:modelValue": ($event: any) => (handleToggle(row as QualificationTransferLink, $event as boolean))
              }, null, 8, ["model-value", "onUpdate:modelValue"])
            ]),
            _: 1
          }, 8, ["rows"])),
    _createVNode(QualificationPolicyModal, {
      "modal-active": showEditModal.value,
      policy: selectedPolicy.value ?? undefined,
      "schedule-id": props.scheduleId,
      "link-id": selectedLinkId.value ?? undefined,
      onClose: handleCloseEditModal,
      onSaved: handlePolicySaved
    }, null, 8, ["modal-active", "policy", "schedule-id", "link-id"])
  ]))
}
}

})